import { useEffect, useMemo, useState } from 'react';
import {
  Inbox, Search, Download, MailOpen, Mail, Trash2, X, Calendar,
  Building2, MessageSquare, Heart, Users, Handshake, FileText as FileIcon, Loader2,
} from 'lucide-react';
import api from '../api';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const typeIcons = {
  rental: Building2,
  contact: MessageSquare,
  newsletter: Mail,
  volunteer: Users,
  partner: Handshake,
  donation: Heart,
  general: Inbox,
};

const typeColors = {
  rental: 'coral',
  contact: 'teal',
  newsletter: 'yellow',
  volunteer: 'teal',
  partner: 'coral',
  donation: 'coral',
  general: 'yellow',
};

const colorClasses = {
  coral: { bg: 'bg-[#E5614C]', soft: 'bg-[#E5614C]/15', text: 'text-[#E5614C]' },
  teal: { bg: 'bg-[#5DCAB5]', soft: 'bg-[#5DCAB5]/15', text: 'text-[#5DCAB5]' },
  yellow: { bg: 'bg-[#FCD968]', soft: 'bg-[#FCD968]/20', text: 'text-[#FCD968]' },
};

export default function FormSubmissions() {
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, last_7_days: 0, by_type: {} });
  const [activeType, setActiveType] = useState('all');
  const [submissions, setSubmissions] = useState([]);
  const [q, setQ] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadTypesAndStats = async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        api.get('/forms/types'),
        api.get('/forms/stats'),
      ]);
      setTypes(tRes.data);
      setStats(sRes.data);
    } catch (e) { /* ignore */ }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeType !== 'all') params.form_type = activeType;
      if (filterUnread) params.is_read = false;
      const { data } = await api.get('/forms/submissions', { params });
      setSubmissions(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTypesAndStats(); }, []);
  useEffect(() => { loadSubmissions(); }, [activeType, filterUnread]);

  const filtered = useMemo(() => {
    if (!q) return submissions;
    const needle = q.toLowerCase();
    return submissions.filter((s) => {
      const blob = JSON.stringify(s.data || {}).toLowerCase();
      return blob.includes(needle);
    });
  }, [submissions, q]);

  const handleExport = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/forms/export.csv${
        activeType !== 'all' ? `?form_type=${activeType}` : ''
      }`;
      const token = localStorage.getItem('gems_admin_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `gems-submissions-${activeType}-${Date.now()}.csv`;
      a.click();
      toast.success('Exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const handleSelect = async (sub) => {
    setSelected(sub);
    if (!sub.is_read) {
      try {
        await api.put(`/forms/submissions/${sub.id}`, { is_read: true });
        setSubmissions((subs) => subs.map((x) => x.id === sub.id ? { ...x, is_read: true } : x));
        loadTypesAndStats();
      } catch (e) { /* ignore */ }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    try {
      await api.delete(`/forms/submissions/${id}`);
      toast.success('Deleted');
      setSubmissions((subs) => subs.filter((x) => x.id !== id));
      setSelected(null);
      loadTypesAndStats();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[11px]">
            <Inbox className="h-3 w-3" /> Form Submissions
          </div>
          <h1 className="font-serif-display text-[36px] lg:text-[42px] font-bold mt-2">Inquiries & messages</h1>
          <p className="text-white/55 text-[14px] mt-1">View, manage, and export all form submissions from the website.</p>
        </div>
        <Button onClick={handleExport} className="h-10 rounded-full bg-white/5 hover:bg-white/10 text-white font-semibold px-5 border border-white/10">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <StatBox label="Total submissions" value={stats.total} icon={Inbox} color="coral" />
        <StatBox label="Unread" value={stats.unread} icon={Mail} color="teal" highlight={stats.unread > 0} />
        <StatBox label="Last 7 days" value={stats.last_7_days} icon={Calendar} color="yellow" />
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar: form types */}
        <aside className="space-y-1">
          <TypeRow
            label="All forms"
            count={stats.total}
            icon={Inbox}
            color="coral"
            active={activeType === 'all'}
            onClick={() => setActiveType('all')}
          />
          {types.filter((t) => t.total > 0).map((t) => {
            const Icon = typeIcons[t.key] || FileIcon;
            return (
              <TypeRow
                key={t.key}
                label={t.label}
                count={t.total}
                unread={t.unread}
                icon={Icon}
                color={typeColors[t.key] || 'coral'}
                active={activeType === t.key}
                onClick={() => setActiveType(t.key)}
              />
            );
          })}
          {types.filter((t) => t.total === 0).length > 0 && (
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-semibold px-3 pb-2">Empty</div>
              {types.filter((t) => t.total === 0).map((t) => {
                const Icon = typeIcons[t.key] || FileIcon;
                return (
                  <TypeRow
                    key={t.key}
                    label={t.label}
                    count={0}
                    icon={Icon}
                    color={typeColors[t.key] || 'coral'}
                    muted
                    active={activeType === t.key}
                    onClick={() => setActiveType(t.key)}
                  />
                );
              })}
            </div>
          )}
        </aside>

        {/* Main list + detail */}
        <div className="space-y-4 min-w-0">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search submissions…"
                className="h-11 pl-10 rounded-xl bg-[#16131a] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
              />
            </div>
            <label className="flex items-center gap-2 px-4 h-11 rounded-xl bg-[#16131a] border border-white/[0.06] cursor-pointer">
              <input type="checkbox" checked={filterUnread} onChange={(e) => setFilterUnread(e.target.checked)} className="accent-[#E5614C]" />
              <span className="text-[13px] text-white/75 font-medium">Unread only</span>
            </label>
          </div>

          <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] overflow-hidden">
            {loading && <div className="p-10 text-center text-white/50 text-[14px]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>}
            {!loading && filtered.length === 0 && (
              <div className="p-14 text-center">
                <div className="h-14 w-14 rounded-2xl bg-[#E5614C]/15 text-[#E5614C] flex items-center justify-center mx-auto">
                  <Inbox className="h-6 w-6" />
                </div>
                <div className="font-serif-display text-[22px] font-bold mt-4">No submissions yet</div>
                <p className="text-white/55 text-[14px] mt-1 max-w-sm mx-auto">
                  When someone submits the rental, contact, or newsletter form, it'll appear here.
                </p>
              </div>
            )}
            {filtered.map((s, i) => {
              const Icon = typeIcons[s.form_type] || FileIcon;
              const cl = colorClasses[typeColors[s.form_type] || 'coral'];
              const summary = getSummary(s);
              return (
                <button
                  key={s.id} onClick={() => handleSelect(s)}
                  className={`group w-full flex items-center gap-4 px-5 py-4 hover:bg-white/[0.04] transition-colors text-left ${i < filtered.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
                >
                  <div className={`h-10 w-10 rounded-lg ${cl.soft} ${cl.text} flex items-center justify-center shrink-0`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold tracking-[0.14em] uppercase ${cl.text}`}>{s.form_label}</span>
                      {!s.is_read && <span className="h-2 w-2 rounded-full bg-[#E5614C] pulse-dot" />}
                    </div>
                    <div className={`mt-1 text-[14px] truncate ${s.is_read ? 'text-white/85' : 'text-white font-semibold'}`}>{summary.title}</div>
                    {summary.subtitle && <div className="text-[12px] text-white/50 truncate mt-0.5">{summary.subtitle}</div>}
                  </div>
                  <div className="text-[11px] text-white/40 shrink-0">{formatDate(s.created_at)}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {selected && (
        <SubmissionDrawer
          submission={selected}
          onClose={() => setSelected(null)}
          onUpdate={(updated) => {
            setSubmissions((subs) => subs.map((x) => x.id === updated.id ? updated : x));
            setSelected(updated);
            loadTypesAndStats();
          }}
          onDelete={() => handleDelete(selected.id)}
        />
      )}
    </div>
  );
}

function TypeRow({ label, count, unread, icon: Icon, color, active, muted, onClick }) {
  const cl = colorClasses[color] || colorClasses.coral;
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 h-11 rounded-xl text-[14px] font-medium transition-colors ${
        active
          ? 'bg-[#E5614C] text-white shadow-[0_8px_20px_-8px_rgba(229,97,76,0.7)]'
          : muted
            ? 'text-white/40 hover:bg-white/5'
            : 'text-white/75 hover:bg-white/5'
      }`}
    >
      <div className={`h-7 w-7 rounded-lg ${active ? 'bg-white/15' : cl.soft} ${active ? 'text-white' : cl.text} flex items-center justify-center shrink-0`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="flex-1 text-left truncate">{label}</span>
      {!!unread && unread > 0 && (
        <span className="text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-[#E5614C]/20 text-[#E5614C]">{unread}</span>
      )}
      <span className={`text-[11px] font-semibold ${active ? 'text-white/85' : 'text-white/45'}`}>{count}</span>
    </button>
  );
}

function StatBox({ label, value, icon: Icon, color, highlight }) {
  const cl = colorClasses[color] || colorClasses.coral;
  return (
    <div className={`rounded-2xl bg-[#16131a] border ${highlight ? 'border-[#E5614C]/40' : 'border-white/[0.06]'} p-6 relative overflow-hidden`}>
      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-50 ${cl.soft}`} />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-white/55 text-[12px] uppercase tracking-[0.16em] font-semibold">{label}</div>
          <div className="font-serif-display text-[44px] font-bold mt-2 leading-none">{value}</div>
        </div>
        <div className={`h-11 w-11 rounded-xl ${cl.bg} ${color === 'yellow' ? 'text-[#2A1B1A]' : 'text-white'} flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function SubmissionDrawer({ submission, onClose, onUpdate, onDelete }) {
  const [notes, setNotes] = useState(submission.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => { setNotes(submission.notes || ''); }, [submission.id, submission.notes]);

  const handleToggleRead = async () => {
    try {
      const { data } = await api.put(`/forms/submissions/${submission.id}`, { is_read: !submission.is_read });
      onUpdate(data);
    } catch (e) { toast.error('Failed'); }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.put(`/forms/submissions/${submission.id}`, { notes });
      onUpdate(data);
      toast.success('Notes saved');
    } catch (e) { toast.error('Failed'); }
    finally { setSavingNotes(false); }
  };

  const fields = Object.entries(submission.data || {});

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl h-full bg-[#16131a] border-l border-white/[0.06] overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-[#16131a] border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">{submission.form_label}</div>
            <div className="font-serif-display text-[20px] font-bold mt-0.5 truncate">{getSummary(submission).title}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/70">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center gap-2 text-[12px] text-white/55">
            <Calendar className="h-3.5 w-3.5" /> {formatFullDate(submission.created_at)}
            {submission.source_page && <span className="ml-2">· from {submission.source_page}</span>}
          </div>

          <div className="flex gap-2">
            <Button onClick={handleToggleRead} variant="ghost" className="h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/75 hover:text-white font-semibold px-4 border border-white/10">
              {submission.is_read ? <><Mail className="h-3.5 w-3.5 mr-1.5" /> Mark Unread</> : <><MailOpen className="h-3.5 w-3.5 mr-1.5" /> Mark Read</>}
            </Button>
            <Button onClick={onDelete} variant="ghost" className="h-9 rounded-full text-white/55 hover:text-red-400 hover:bg-red-500/10 font-semibold px-4">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          </div>

          {/* Field display */}
          <div className="rounded-2xl bg-[#0f0d12] border border-white/[0.06] divide-y divide-white/[0.04]">
            {fields.length === 0 && <div className="p-5 text-white/50 text-[14px]">No data fields.</div>}
            {fields.map(([k, v]) => (
              <div key={k} className="px-5 py-3.5">
                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-white/45">{prettyKey(k)}</div>
                <div className="mt-1 text-[14px] text-white/85 break-words whitespace-pre-wrap">{formatValue(v)}</div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-white/75">Internal notes</div>
              <Button onClick={handleSaveNotes} disabled={savingNotes || notes === (submission.notes || '')} className="h-8 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold text-[12px] px-3 disabled:opacity-40">
                {savingNotes ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save Notes'}
              </Button>
            </div>
            <Textarea
              rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes about this submission… (e.g. follow-up status, replied date)"
              className="rounded-lg bg-[#0f0d12] border-white/10 text-white text-[13px] placeholder:text-white/30 resize-none leading-relaxed focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function getSummary(s) {
  const d = s.data || {};
  if (s.form_type === 'rental') {
    return { title: d.name || d.organization || 'Rental inquiry', subtitle: [d.eventType, d.preferredDates].filter(Boolean).join(' · ') };
  }
  if (s.form_type === 'contact') {
    const name = [d.firstName, d.lastName].filter(Boolean).join(' ');
    return { title: name || d.email || 'Contact', subtitle: d.interest || d.message || '' };
  }
  if (s.form_type === 'newsletter') {
    return { title: d.email || 'Newsletter signup', subtitle: '' };
  }
  return { title: d.name || d.email || 'Submission', subtitle: d.message || '' };
}

function prettyKey(k) {
  return k.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/\s+/g, ' ').trim();
}

function formatValue(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return dt.toLocaleDateString();
}

function formatFullDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
